import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../../axiosConfig";
import UserSelect from "./components/UserSelect";
import PDFCoverPreview, { coverStreamUrl } from "../../components/PDFCoverPreview";
import TagsFilter, {
  bookMatchesSelectedTags,
  mergeTagOptions,
  tagsFromBooks,
} from "../../components/TagsFilter";
import "../../styles/manage-user-css/ManageAccess.css";

const CHIP_TONES = ["primary", "mint", "sky", "lilac", "peach"];

const STAT_BOOK_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const FILTER_DEFS = [
  { key: "subjects", label: "Subject", idKey: "subject_id", nameKey: "subject_name", style: "checkbox" },
  { key: "grades", label: "Grade", idKey: "grade_id", nameKey: "grade_level", style: "pills" },
  { key: "bookTypes", label: "Book Type", idKey: "book_type_id", nameKey: "book_type_title", style: "checkbox" },
  { key: "versions", label: "Version", idKey: "version_id", nameKey: "version_name", style: "checkbox" },
  { key: "languages", label: "Language", idKey: "language_id", nameKey: "language_name", style: "checkbox" },
  { key: "countries", label: "Country", idKey: "country_id", nameKey: "country_name", style: "checkbox" },
];

const VERSION_FILTER_OPTIONS = [
  { version_id: "Digital", version_name: "Digital" },
  { version_id: "Print", version_name: "Print" },
];

const createInitialFilters = () => ({
  grades: new Set(),
  subjects: new Set(),
  languages: new Set(),
  bookTypes: new Set(),
  versions: new Set(),
  countries: new Set(),
  tags: [],
});

const formatExpiryForInput = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const yyyy = value.getUTCFullYear();
    const mm = String(value.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(value.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const yyyy = parsed.getUTCFullYear();
    const mm = String(parsed.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(parsed.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
};

const toDownloadFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const SuccessPopup = ({ message, onClose, isError = false }) => (
  <div className="success-popup-overlay">
    <div className={`success-popup${isError ? " is-error" : ""}`}>
      <div className="popup-icon">{isError ? "!" : "✓"}</div>
      <div className="popup-message">{message}</div>
      <button type="button" className="popup-close" onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  </div>
);

const flattenAssignedRows = (groups) =>
  groups
    .map((group) => {
      const bookIds = [];
      const formats = [];
      if (group.digital?.book_id) {
        bookIds.push(Number(group.digital.book_id));
        formats.push("Digital");
      }
      if (group.print?.book_id) {
        bookIds.push(Number(group.print.book_id));
        formats.push("Print");
      }
      if (!bookIds.length && group.book_id) {
        bookIds.push(Number(group.book_id));
        formats.push("Digital");
      }
      return {
        key: [group.title, group.isbn_code, group.grade_id, group.version_label]
          .map((v) => (v == null ? "" : String(v)))
          .join("|"),
        bookIds: [...new Set(bookIds.filter(Boolean))],
        title: group.title,
        version: group.version_label,
        isbn: group.isbn_code,
        formats,
      };
    })
    .filter((row) => row.bookIds.length > 0);

const setHasId = (set, value) => {
  if (!set || value === undefined || value === null || value === "") return false;
  return set.has(value) || set.has(String(value)) || set.has(Number(value));
};

const filterAllows = (set, value) => {
  if (!set || set.size === 0) return true;
  return setHasId(set, value);
};

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items = [1];
  if (current > 3) items.push("…");
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) {
    if (!items.includes(p)) items.push(p);
  }
  if (current < total - 2) items.push("…");
  if (!items.includes(total)) items.push(total);
  return items;
}

function gradeRank(label) {
  const text = String(label || "").trim();
  const lower = text.toLowerCase();
  const num = (text.match(/(\d+)/) || [0, 0])[1] * 1 || 0;
  if (/\bkg\b|kindergarten|nursery|pre[-\s]?k/.test(lower)) return [0, num];
  if (/grade|year|class/.test(lower)) return [1, num];
  if (/elective/.test(lower)) return [2, num];
  return [3, num];
}

function sortGradeRecords(grades) {
  return [...(grades || [])].sort((a, b) => {
    const [ga, na] = gradeRank(a.grade_level);
    const [gb, nb] = gradeRank(b.grade_level);
    if (ga !== gb) return ga - gb;
    if (na !== nb) return na - nb;
    return String(a.grade_level || "").localeCompare(String(b.grade_level || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
}

const AssignedBooksPanel = ({
  assignedBooks,
  edits,
  onEditChange,
  onSaveAll,
  onUnassign,
  onBulkUnassign,
  saving,
  unassigning,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const pageSize = 5;

  const filteredBooks = assignedBooks.filter((group) => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return true;
    return (
      group.title?.toLowerCase().includes(search) ||
      group.version_label?.toLowerCase().includes(search) ||
      group.isbn_code?.toLowerCase().includes(search) ||
      (group.digital && "digital".includes(search)) ||
      (group.print && "print".includes(search))
    );
  });

  const assignedRows = flattenAssignedRows(filteredBooks);
  const totalPages = Math.ceil(assignedRows.length / pageSize) || 1;
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedRows = assignedRows.slice(startIdx, startIdx + pageSize);
  const pageKeys = paginatedRows.map((row) => row.key);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((key) => selectedIds.includes(key));
  const somePageSelected = pageKeys.some((key) => selectedIds.includes(key));
  const downloadIds = assignedRows.flatMap((row) => row.bookIds);
  const allDownloadsOn =
    downloadIds.length > 0 && downloadIds.every((id) => toDownloadFlag(edits[id]?.can_download));
  const someDownloadsOn = downloadIds.some((id) => toDownloadFlag(edits[id]?.can_download));

  const rowDownloadOn = (row) =>
    row.bookIds.length > 0 && row.bookIds.every((id) => toDownloadFlag(edits[id]?.can_download));
  const rowDownloadPartial = (row) =>
    row.bookIds.some((id) => toDownloadFlag(edits[id]?.can_download)) && !rowDownloadOn(row);
  const rowExpiry = (row) => {
    for (const id of row.bookIds) {
      if (edits[id]?.expiry) return edits[id].expiry;
    }
    return "";
  };

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [assignedRows.length, currentPage, totalPages]);

  useEffect(() => {
    const validKeys = new Set(flattenAssignedRows(assignedBooks).map((row) => row.key));
    setSelectedIds((prev) => {
      const next = prev.filter((key) => validKeys.has(key));
      return next.length === prev.length ? prev : next;
    });
  }, [assignedBooks]);

  const updateRow = (bookIds, changes) => {
    const nextEdits = { ...edits };
    bookIds.forEach((bookId) => {
      const id = Number(bookId);
      nextEdits[id] = { ...nextEdits[id], ...changes };
    });
    onEditChange(nextEdits);
  };

  const toggleRow = (rowKey) => {
    setSelectedIds((prev) =>
      prev.includes(rowKey) ? prev.filter((x) => x !== rowKey) : [...prev, rowKey]
    );
  };

  const togglePage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((key) => !pageKeys.includes(key)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...pageKeys])]);
  };

  const toggleAllDownloads = () => {
    if (!downloadIds.length) return;
    const nextValue = !allDownloadsOn;
    const nextEdits = { ...edits };
    downloadIds.forEach((id) => {
      nextEdits[id] = { ...nextEdits[id], can_download: nextValue };
    });
    onEditChange(nextEdits);
  };

  const handleBulkUnassignClick = async () => {
    const rows = assignedRows.filter((row) => selectedIds.includes(row.key));
    if (!rows.length) return;
    const ok = await onBulkUnassign(rows);
    if (ok) setSelectedIds([]);
  };

  return (
    <div className="panel" style={{ marginBottom: 18 }}>
      <div className="panel-head">
        <span className="panel-title">Assigned books</span>
        <div className="btn-row">
          <input
            className="text-input"
            placeholder="Search by title or ISBN…"
            style={{ minWidth: 240 }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <button
            type="button"
            className="btn ma-bulk-unassign-btn"
            onClick={handleBulkUnassignClick}
            disabled={!selectedIds.length || unassigning}
          >
            {unassigning
              ? "Unassigning…"
              : `Unassign Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
          </button>
          <button type="button" className="btn btn-primary" onClick={onSaveAll} disabled={saving}>
            {saving ? "Saving…" : "Save All Changes"}
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 42 }}>
                <input
                  type="checkbox"
                  className="checkbox-select"
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allPageSelected && somePageSelected;
                  }}
                  onChange={togglePage}
                  aria-label="Select all books on this page"
                  disabled={!paginatedRows.length}
                />
              </th>
              <th>#</th>
              <th>Book title</th>
              <th>Version</th>
              <th>ISBN</th>
              <th>Format</th>
              <th className="ma-download-th">
                <label className="ma-download-all">
                  <input
                    type="checkbox"
                    className="checkbox-select"
                    checked={allDownloadsOn}
                    ref={(el) => {
                      if (el) el.indeterminate = !allDownloadsOn && someDownloadsOn;
                    }}
                    onChange={toggleAllDownloads}
                    aria-label="Allow download for all assigned books"
                    disabled={!downloadIds.length}
                  />
                  <span>Download</span>
                </label>
              </th>
              <th>Expiry</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="empty-note">
                    {searchTerm
                      ? "No assigned books match your search."
                      : "No books assigned to this user yet."}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, index) => {
                const isChecked = selectedIds.includes(row.key);
                const downloadChecked = rowDownloadOn(row);
                const downloadPartial = rowDownloadPartial(row);
                return (
                  <tr key={row.key} className={isChecked ? "ma-row-selected" : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox-select"
                        checked={isChecked}
                        onChange={() => toggleRow(row.key)}
                        aria-label={`Select ${row.title}`}
                      />
                    </td>
                    <td>{startIdx + index + 1}</td>
                    <td className="cell-title">{row.title || "—"}</td>
                    <td>{row.version || "—"}</td>
                    <td>{row.isbn || "—"}</td>
                    <td>
                      <div className="ma-format-chips">
                        {row.formats.map((format) => (
                          <span
                            key={`${row.key}-${format}`}
                            className={`chip ${format === "Print" ? "chip-peach" : "chip-primary"}`}
                          >
                            {format}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="ma-download-td">
                      <input
                        type="checkbox"
                        className="checkbox-select"
                        checked={downloadChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = downloadPartial;
                        }}
                        onChange={(e) => updateRow(row.bookIds, { can_download: e.target.checked })}
                        aria-label={`Allow download for ${row.title}`}
                      />
                    </td>
                    <td>
                      <input
                        className="text-input"
                        type="date"
                        style={{ minWidth: 150, padding: "7px 10px" }}
                        value={rowExpiry(row)}
                        onChange={(e) => updateRow(row.bookIds, { expiry: e.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-quiet ma-unassign-btn"
                        disabled={unassigning}
                        onClick={() => onUnassign(row)}
                        title="Unassign this book from the selected user"
                      >
                        Unassign
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {assignedRows.length > 0 && (
        <div className="pagination">
          <span className="page-info">
            Showing {startIdx + 1}–{Math.min(startIdx + pageSize, assignedRows.length)} of{" "}
            {assignedRows.length}
            {selectedIds.length ? ` · ${selectedIds.length} selected` : ""}
          </span>
          <button
            type="button"
            className="page-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            ‹
          </button>
          {pageItems(currentPage, totalPages).map((item, idx) =>
            item === "…" ? (
              <button key={`e-${idx}`} type="button" className="page-btn" disabled>
                …
              </button>
            ) : (
              <button
                key={item}
                type="button"
                className={`page-btn${item === currentPage ? " active" : ""}`}
                onClick={() => setCurrentPage(item)}
              >
                {item}
              </button>
            )
          )}
          <button
            type="button"
            className="page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

const AssignNewBooksPanel = ({ unassignedBooks, onAssign, filters, assigning }) => {
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const filteredBooks = unassignedBooks.filter((book) => {
    const search = searchTerm.toLowerCase().trim();
    const searchMatch =
      !search ||
      book.title?.toLowerCase().includes(search) ||
      book.description?.toLowerCase().includes(search) ||
      book.grade_level?.toLowerCase().includes(search) ||
      book.subject_name?.toLowerCase().includes(search) ||
      book.language_name?.toLowerCase().includes(search) ||
      book.country_name?.toLowerCase().includes(search) ||
      book.version_label?.toLowerCase().includes(search) ||
      book.isbn_code?.toLowerCase().includes(search) ||
      book.tags?.some((tag) => tag.tag_name?.toLowerCase().includes(search));

    if (!searchMatch) return false;

    const matchGrade = filterAllows(filters.grades, book.grade_id);
    const matchSubject = filterAllows(filters.subjects, book.subject_id);
    const matchLanguage = filterAllows(filters.languages, book.language_id);
    const matchBookType = filterAllows(filters.bookTypes, book.booktype_id);
    const matchCountry = filterAllows(filters.countries, book.country_id);
    const selectedVersions = filters.versions || new Set();
    const matchVersion =
      selectedVersions.size === 0 ||
      [...selectedVersions].some(
        (v) =>
          (String(v) === "Digital" && book.digital) ||
          (String(v) === "Print" && book.print)
      );

    return matchGrade && matchSubject && matchLanguage && matchBookType && matchVersion && matchCountry && bookMatchesSelectedTags(book, filters.tags || []);
  });

  const totalPages = Math.max(1, Math.ceil(filteredBooks.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pagedBooks = filteredBooks.slice(pageStart, pageStart + pageSize);
  const showingFrom = filteredBooks.length ? pageStart + 1 : 0;
  const showingTo = Math.min(pageStart + pageSize, filteredBooks.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const getGroupKey = (group) =>
    [group.title, group.isbn_code, group.grade_id, group.version_label, group.book_id].join("|");

  const toggle = (group) => {
    const key = getGroupKey(group);
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const assign = async () => {
    const groups = selected
      .map((key) => unassignedBooks.find((g) => getGroupKey(g) === key))
      .filter(Boolean);
    if (!groups.length) return;
    const ok = await onAssign(groups);
    if (ok) setSelected([]);
  };

  return (
    <div className="assign-new-books">
      <div className="panel-head assign-new-head">
        <span className="panel-title">Assign new books</span>
        <button
          type="button"
          className="btn btn-primary"
          onClick={assign}
          disabled={!selected.length || assigning}
        >
          {assigning ? "Assigning…" : `Assign Selected${selected.length ? ` (${selected.length})` : ""}`}
        </button>
      </div>

      <div className="assign-new-toolbar">
        <input
          type="text"
          className="text-input"
          placeholder="Search by title, description, or tag…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <p className="assign-new-showing">
          Showing {showingFrom}–{showingTo} of {filteredBooks.length} {filteredBooks.length === 1 ? "book" : "books"}
        </p>
      </div>

      {!filteredBooks.length ? (
        <div className="empty-note">No unassigned books available for selected filters.</div>
      ) : (
        <>
        <div className="explore-grid">
          {pagedBooks.map((book) => {
            const main = book.digital || book.print;
            if (!main) return null;
            const key = getGroupKey(book);
            const isSelected = selected.includes(key);
            const title = book.title || "Untitled";
            const gradeLabel = book.grade_level || null;
            const pillChips = [];
            if (book.book_type_title) pillChips.push(book.book_type_title);
            if (book.subject_name) pillChips.push(book.subject_name);
            if (book.country_name) pillChips.push(book.country_name);
            if (book.language_name) pillChips.push(book.language_name);
            if (book.version_label) pillChips.push(book.version_label);
            book.tags?.slice(0, 2).forEach((tag) => {
              if (tag?.tag_name) pillChips.push(tag.tag_name);
            });

            return (
              <article
                key={key}
                className={`explore-card assign-explore-card${isSelected ? " is-selected" : ""}`}
                onClick={() => toggle(book)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(book);
                  }
                }}
              >
                <div className="explore-cover">
                  <label
                    className={`assign-card-check-wrap${isSelected ? " is-checked" : ""}`}
                    onClick={(e) => e.stopPropagation()}
                    title={isSelected ? "Deselect book" : "Select book"}
                  >
                    <input
                      type="checkbox"
                      className="assign-card-check"
                      checked={isSelected}
                      onChange={() => toggle(book)}
                      aria-label={`Select ${title}`}
                    />
                    <span className="assign-card-check-box" aria-hidden="true" />
                  </label>
                  <PDFCoverPreview
                    pdfUrl={coverStreamUrl(
                      main.book_id,
                      book.last_updated_at || main.last_updated_at
                    )}
                    height={300}
                    bookTitle={title}
                  />
                  {(book.digital || book.print) && (
                    <div className="explore-format-badges">
                      {book.digital ? <span className="explore-format-badge">Digital</span> : null}
                      {book.print ? <span className="explore-format-badge">Print</span> : null}
                    </div>
                  )}
                </div>
                <div className="explore-body">
                  <h3 className="explore-title" title={title}>
                    {title}
                  </h3>
                  <div className="explore-tags">
                    {gradeLabel ? <span className="explore-meta-plain">{gradeLabel}</span> : null}
                    {pillChips.slice(0, 4).map((chip, i) => (
                      <span key={`${chip}-${i}`} className={`chip chip-${CHIP_TONES[i % CHIP_TONES.length]}`}>
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        <div className="pagination">
          <span className="page-info">
            Showing {showingFrom} to {showingTo} of {filteredBooks.length} books
            {selected.length ? ` · ${selected.length} selected` : ""}
          </span>
          {totalPages > 1 && (
            <>
              <button
                type="button"
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={safePage === 1}
                aria-label="Previous page"
              >
                ‹
              </button>
              {pageItems(safePage, totalPages).map((item, idx) =>
                item === "…" ? (
                  <button key={`e-${idx}`} type="button" className="page-btn" disabled>
                    …
                  </button>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`page-btn${item === safePage ? " active" : ""}`}
                    onClick={() => setCurrentPage(item)}
                  >
                    {item}
                  </button>
                )
              )}
              <button
                type="button"
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={safePage === totalPages}
                aria-label="Next page"
              >
                ›
              </button>
            </>
          )}
        </div>
        </>
      )}
    </div>
  );
};

const FilterAside = ({ filters, setFilters, metadata, tagOptions = [] }) => {
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries([...FILTER_DEFS.map(({ key }) => [key, true]), ["tags", true]])
  );

  const toggleSelection = (key, id) => {
    setFilters((prev) => {
      const next = new Set(prev[key]);
      const variants = [id, String(id), Number(id)];
      const exists = variants.some((v) => next.has(v));
      if (exists) {
        variants.forEach((v) => next.delete(v));
      } else {
        next.add(id);
      }
      return { ...prev, [key]: next };
    });
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClear = () => setFilters(createInitialFilters());

  return (
    <aside>
      <div className="filter-card explore-filter-panel">
        <div className="explore-filter-head">
          <div className="explore-filter-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span>Filters</span>
          </div>
          <button type="button" className="explore-filter-clear" onClick={handleClear}>
            Clear all
          </button>
        </div>

        <div className="explore-filter-body">
          <section className={`explore-filter-section${openSections.tags !== false ? " is-open" : ""}`}>
            <button
              type="button"
              className="explore-filter-section-toggle"
              onClick={() => toggleSection("tags")}
              aria-expanded={openSections.tags !== false}
            >
              <span className="explore-filter-section-title">
                Tags
                {(filters.tags || []).length > 0 ? (
                  <span className="explore-filter-count">{(filters.tags || []).length}</span>
                ) : null}
              </span>
              <svg
                className="explore-filter-chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {openSections.tags !== false ? (
              <TagsFilter
                tagOptions={tagOptions}
                selected={filters.tags || []}
                onChange={(next) => setFilters((prev) => ({ ...prev, tags: next }))}
              />
            ) : null}
          </section>

          {FILTER_DEFS.map(({ key, label, idKey, nameKey, style }) => {
            const items = metadata[key] || [];
            if (!items.length) return null;
            const selected = filters[key] || new Set();
            const isOpen = openSections[key] !== false;
            const selectedCount = selected.size;

            return (
              <section key={key} className={`explore-filter-section${isOpen ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="explore-filter-section-toggle"
                  onClick={() => toggleSection(key)}
                  aria-expanded={isOpen}
                >
                  <span className="explore-filter-section-title">
                    {label}
                    {selectedCount > 0 ? (
                      <span className="explore-filter-count">{selectedCount}</span>
                    ) : null}
                  </span>
                  <svg
                    className="explore-filter-chevron"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isOpen ? (
                  style === "pills" ? (
                    <div className={`explore-filter-pills${key === "grades" ? " is-scrollable" : ""}`}>
                      {items.map((item) => {
                        const id = item[idKey];
                        const active = setHasId(selected, id);
                        return (
                          <button
                            key={id}
                            type="button"
                            className={`explore-filter-pill ${active ? "is-active" : ""}`}
                            onClick={() => toggleSelection(key, id)}
                            title={item[nameKey]}
                          >
                            {item[nameKey]}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <ul className="explore-filter-checks">
                      {items.map((item) => {
                        const id = item[idKey];
                        const checked = setHasId(selected, id);
                        return (
                          <li key={id}>
                            <label className={`explore-check ${checked ? "is-checked" : ""}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSelection(key, id)}
                              />
                              <span className="explore-check-box" aria-hidden="true" />
                              <span className="explore-check-label">{item[nameKey]}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )
                ) : null}
              </section>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

function groupBooksByLogicalBook(books) {
  const mergeTags = (a = [], b = []) => {
    const map = new Map();
    [...(a || []), ...(b || [])].forEach((t) => {
      if (!t) return;
      const key =
        t.tag_id != null && t.tag_id !== ""
          ? `id:${t.tag_id}`
          : `name:${String(t.tag_name || "").toLowerCase()}`;
      if (!map.has(key)) map.set(key, t);
    });
    return [...map.values()];
  };

  const grouped = {};
  for (const book of books) {
    // Group digital/print variants of the same logical book together
    const key = [book.title, book.isbn_code, book.grade_id, book.version_label]
      .map((v) => (v == null ? "" : String(v)))
      .join("|");
    if (!grouped[key]) {
      grouped[key] = {
        title: book.title,
        isbn_code: book.isbn_code,
        grade_id: book.grade_id,
        grade_level: book.grade_level,
        version_label: book.version_label,
        book_id: book.book_id,
        digital: null,
        print: null,
        description: book.description,
        subject_id: book.subject_id,
        subject_name: book.subject_name,
        language_id: book.language_id,
        language_name: book.language_name,
        standard_id: book.standard_id,
        standard_name: book.standard_name,
        country_id: book.country_id,
        country_name: book.country_name,
        booktype_id: book.booktype_id,
        book_type_title: book.book_type_title,
        created_by: book.created_by,
        created_at: book.created_at,
        tags: book.tags || [],
      };
    } else {
      grouped[key].tags = mergeTags(grouped[key].tags, book.tags);
    }
    const formatName = (book.format_name || "").toLowerCase();
    if (formatName.includes("digital")) {
      grouped[key].digital = book;
    } else if (formatName.includes("print")) {
      grouped[key].print = book;
    } else if (!grouped[key].digital) {
      grouped[key].digital = book;
    }
    if (!grouped[key].book_id) grouped[key].book_id = book.book_id;
  }
  return Object.values(grouped);
}

const ManageAccess = () => {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [assignedBooks, setAssignedBooks] = useState([]);
  const [unassignedBooks, setUnassignedBooks] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [assignedBookEdits, setAssignedBookEdits] = useState({});
  const [assigning, setAssigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unassigning, setUnassigning] = useState(false);

  const [filters, setFilters] = useState(createInitialFilters);

  const [metadata, setMetadata] = useState({
    grades: [],
    subjects: [],
    languages: [],
    bookTypes: [],
    versions: VERSION_FILTER_OPTIONS,
    countries: [],
  });
  const [tagOptions, setTagOptions] = useState([]);

  const token = localStorage.getItem("token");
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: token ? `Bearer ${token}` : "" } }),
    [token]
  );

  const loadAccess = useCallback(async (userId) => {
    try {
      const res = await axios.get(`/books/user-access/${userId}`, authHeaders);
      const assigned = res.data.assignedBooks || [];
      const unassigned = groupBooksByLogicalBook(res.data.unassignedBooks || []);
      setAssignedBooks(groupBooksByLogicalBook(assigned));
      setUnassignedBooks(unassigned);
      setTagOptions(
        mergeTagOptions(
          tagsFromBooks(unassigned),
          tagsFromBooks(groupBooksByLogicalBook(assigned))
        )
      );
      const initialEdits = {};
      assigned.forEach((book) => {
        initialEdits[Number(book.book_id)] = {
          can_download: toDownloadFlag(book.can_download),
          expiry: formatExpiryForInput(book.expiry),
        };
      });
      setAssignedBookEdits(initialEdits);
    } catch (err) {
      console.error("Error loading access data:", err);
      setErrorMessage("Failed to load user book access.");
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!token) return;
    axios
      .get("/user", authHeaders)
      .then((res) => {
        const filtered = res.data.users?.filter((u) => u.role !== "admin") || [];
        setUsers(filtered);
        const preset = searchParams.get("user");
        if (preset && filtered.some((u) => String(u.user_id) === String(preset))) {
          setSelectedUserId(String(preset));
        }
      })
      .catch((err) => console.error("Error loading users:", err));
  }, [token, authHeaders, searchParams]);

  useEffect(() => {
    if (!selectedUserId) {
      setAssignedBooks([]);
      setUnassignedBooks([]);
      setAssignedBookEdits({});
      setTagOptions([]);
      setFilters(createInitialFilters());
      return;
    }
    setFilters(createInitialFilters());
    loadAccess(selectedUserId);
  }, [selectedUserId, loadAccess]);

  useEffect(() => {
    if (!token) return;

    const fetchMetadata = async () => {
      try {
        const [gradesRes, subjectsRes, languagesRes, bookTypesRes, countriesRes] =
          await Promise.all([
            axios.get("/grades", authHeaders),
            axios.get("/subjects", authHeaders),
            axios.get("/languages", authHeaders),
            axios.get("/booktypes", authHeaders),
            axios.get("/countries", authHeaders).catch(() => ({ data: [] })),
          ]);

        setMetadata((prev) => ({
          ...prev,
          grades: sortGradeRecords(gradesRes.data || []),
          subjects: subjectsRes.data || [],
          languages: languagesRes.data || [],
          bookTypes: (bookTypesRes.data || []).map((bt) => ({
            ...bt,
            book_type_id: bt.book_type_id ?? bt.booktype_id,
          })),
          versions: VERSION_FILTER_OPTIONS,
          countries: countriesRes.data || [],
        }));
      } catch (error) {
        console.error("Error fetching filter metadata:", error);
      }
    };

    fetchMetadata();
  }, [token, authHeaders]);

  const handleBulkUpdate = async () => {
    if (!selectedUserId) return;
    const entries = Object.entries(assignedBookEdits);
    if (!entries.length) {
      setErrorMessage("No assigned book changes to save.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      await Promise.all(
        entries.map(([bookId, changes]) => {
          const payload = {
            user_id: parseInt(selectedUserId, 10),
            book_id: parseInt(bookId, 10),
            permission: "viewer",
            expiry: changes.expiry ? String(changes.expiry).slice(0, 10) : null,
            can_download: toDownloadFlag(changes.can_download),
          };
          return axios.post("/books/book-control", payload, authHeaders);
        })
      );
      await loadAccess(selectedUserId);
      setSuccessMessage("Download and expiry settings saved for this user.");
    } catch (err) {
      console.error("Error saving all changes:", err);
      setErrorMessage(err.response?.data?.error || "Failed to save download/expiry changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (groups) => {
    if (!selectedUserId) {
      setErrorMessage("Select a user before assigning books.");
      return false;
    }

    const list = Array.isArray(groups) ? groups : [groups];
    const bookIds = [];
    list.forEach((group) => {
      if (group?.digital?.book_id) bookIds.push(group.digital.book_id);
      if (group?.print?.book_id) bookIds.push(group.print.book_id);
      if (!group?.digital && !group?.print && group?.book_id) bookIds.push(group.book_id);
    });

    const uniqueIds = [...new Set(bookIds.map((id) => Number(id)).filter(Boolean))];
    if (!uniqueIds.length) {
      setErrorMessage("No book formats found to assign.");
      return false;
    }

    setAssigning(true);
    setErrorMessage("");
    try {
      await Promise.all(
        uniqueIds.map((bookId) => {
          const payload = {
            user_id: parseInt(selectedUserId, 10),
            book_id: bookId,
            permission: "viewer",
            expiry: null,
            can_download: false,
          };
          return axios.post("/books/book-control", payload, authHeaders);
        })
      );
      await loadAccess(selectedUserId);
      setSuccessMessage(
        uniqueIds.length === 1
          ? "Book assigned to the selected user. Set Download/Expiry above, then Save All Changes."
          : `${uniqueIds.length} books assigned to the selected user. Set Download/Expiry above, then Save All Changes.`
      );
      return true;
    } catch (err) {
      console.error("Error assigning books:", err);
      setErrorMessage(err.response?.data?.error || "Failed to assign books to this user.");
      return false;
    } finally {
      setAssigning(false);
    }
  };

  const unassignBookIds = async (bookIds) => {
    const uniqueIds = [...new Set(bookIds.map((id) => parseInt(id, 10)).filter(Boolean))];
    if (!selectedUserId || !uniqueIds.length) return false;

    setUnassigning(true);
    setErrorMessage("");
    try {
      await Promise.all(
        uniqueIds.map((bookId) =>
          axios.post(
            "/books/book-control/unshare",
            {
              user_id: parseInt(selectedUserId, 10),
              book_id: bookId,
            },
            authHeaders
          )
        )
      );
      await loadAccess(selectedUserId);
      setSuccessMessage(
        uniqueIds.length === 1
          ? "Book unassigned. It is available again under Assign new books."
          : `${uniqueIds.length} books unassigned. They are available again under Assign new books.`
      );
      return true;
    } catch (err) {
      console.error("Error unassigning book(s):", err);
      setErrorMessage(err.response?.data?.error || "Failed to unassign selected book(s).");
      return false;
    } finally {
      setUnassigning(false);
    }
  };

  const handleUnassign = async (row) => {
    if (!row?.bookIds?.length) return;
    const formatLabel = row.formats?.length ? row.formats.join(" + ") : "all formats";
    const confirmed = window.confirm(
      `Unassign "${row.title || "this book"}" (${formatLabel}) from the selected user?`
    );
    if (!confirmed) return;
    await unassignBookIds(row.bookIds);
  };

  const handleBulkUnassign = async (rows) => {
    if (!rows?.length) return false;
    const confirmed = window.confirm(
      `Unassign ${rows.length} selected book${rows.length === 1 ? "" : "s"} from the selected user?`
    );
    if (!confirmed) return false;
    return unassignBookIds(rows.flatMap((row) => row.bookIds || []));
  };

  return (
    <>
      <div className="manage-access-container">
        <div className="page-head">
          <div>
            <h1 className="page-title">Manage User Access</h1>
            <p className="page-desc">
              Choose a user, then control exactly which books they can open and download.
            </p>
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom: 20 }}>
          <div className="panel">
            <UserSelect users={users} selected={selectedUserId} onChange={setSelectedUserId} />
          </div>
          <div className="panel" style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              className="stat-ic"
              style={{
                background: "var(--primary-tint)",
                color: "var(--primary-ink)",
                width: 44,
                height: 44,
              }}
            >
              {STAT_BOOK_ICON}
            </div>
            <div>
              <div style={{ fontSize: 12.5, color: "var(--ink-faint)", fontWeight: 600 }}>
                Assigned books
              </div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 600 }}>
                {flattenAssignedRows(assignedBooks).length}
              </div>
            </div>
          </div>
        </div>

        {selectedUserId && (
          <>
            <AssignedBooksPanel
              key={`assigned-${selectedUserId}`}
              assignedBooks={assignedBooks}
              edits={assignedBookEdits}
              onEditChange={setAssignedBookEdits}
              onSaveAll={handleBulkUpdate}
              onUnassign={handleUnassign}
              onBulkUnassign={handleBulkUnassign}
              saving={saving}
              unassigning={unassigning}
            />

            <div className="layout-with-filters explore-books-layout assign-new-layout">
              <FilterAside
                key={`filters-${selectedUserId}`}
                filters={filters}
                setFilters={setFilters}
                metadata={metadata}
                tagOptions={tagOptions}
              />
              <AssignNewBooksPanel
                key={`assign-${selectedUserId}`}
                unassignedBooks={unassignedBooks}
                onAssign={handleAssign}
                filters={filters}
                assigning={assigning}
              />
            </div>
          </>
        )}
      </div>

      {errorMessage && (
        <SuccessPopup
          message={errorMessage}
          isError
          onClose={() => setErrorMessage("")}
        />
      )}

      {successMessage && (
        <SuccessPopup message={successMessage} onClose={() => setSuccessMessage("")} />
      )}
    </>
  );
};

export default ManageAccess;
